import { Box, Button, TextField, InputAdornment, FormControl, InputLabel, Select, MenuItem, Typography } from "@mui/material";
import { useState, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import UserManagementTable from "./UserManagementTable";
import UserFormDilogContainer from "./UserFormDilogContainer";
import { actionTypes } from "../../store/actionTypes/actionTypes";
import { ADMIN } from "../../constants/admin";
import { getDataFromApi } from "../../services/api/apiService";
import { RestfulUrls } from "../../constants/restfulUrls";
import { IApiResponse } from "../../interfaces/common/IApiResponse";
import LoaderWithText from "../common/ui/LoaderWithText";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import AddIcon from "@mui/icons-material/Add";

const UserManagementContainer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [users, setUsers] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [roleFilter, setRoleFilter] = useState<string>("all");

  const handleNewUserButtonClick = () => {
    dispatch({
      type: actionTypes.UPDATE_USER_FORM_OPEN,
      payload: true,
    });
    dispatch({
      type: actionTypes.UPDATE_USER_FORM_TYPE,
      payload: ADMIN.MEETING_FORM_TYPE.CREATE,
    });
    dispatch({
      type: actionTypes.UPDATE_SELECTED_USER_DATA,
      payload: null,
    });
  };

  const dummyModalCloseTw = () => {};

  const getUsers = async () => {
    // Construct URL properly - BASE_URL already has trailing slash
    const url = `${RestfulUrls.BASE_URL}${RestfulUrls.GET_ALL_USERS}`;
    console.log("Fetching users from URL:", url);
    console.log("BASE_URL:", RestfulUrls.BASE_URL);
    console.log("GET_ALL_USERS:", RestfulUrls.GET_ALL_USERS);
    
    setLoading(true);
    try {
      await getDataFromApi<any>(
        url,
        (response: IApiResponse<any>) => {
          console.log("Users API response:", response);
          // Handle different response structures
          let usersData: any[] = [];
          if (Array.isArray(response.data)) {
            usersData = response.data;
          } else if (response.data && Array.isArray(response.data.data)) {
            usersData = response.data.data;
          } else if (response.data && Array.isArray(response.data.users)) {
            usersData = response.data.users;
          } else {
            // Default to empty array if structure is unexpected
            console.warn("Unexpected response structure:", response.data);
            usersData = [];
          }
          setUsers(usersData);
          setLoading(false);
        },
        { isAuthRequired: true },
        navigate,
        (error: any) => {
          console.error("Error fetching users:", error);
          console.error("Error details:", error?.response?.data);
          console.error("Error status:", error?.response?.status);
          setLoading(false);
        }
      );
    } catch (error) {
      console.error("Exception in getUsers:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    getUsers();
  }, []);

  // Filter users based on search query and role filter
  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      const matchesSearch =
        searchQuery === "" ||
        user.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesRole = roleFilter === "all" || user.role === roleFilter;
      
      return matchesSearch && matchesRole;
    });
  }, [users, searchQuery, roleFilter]);

  // Get unique roles for filter dropdown
  const uniqueRoles = useMemo(() => {
    const roles = new Set(users.map((user) => user.role));
    return Array.from(roles).sort();
  }, [users]);

  return (
    <>
      <Box className='user-management-container'>
        {/* Header Section */}
        <Box className='user-management-header'>
          <Box>
            <Typography variant="h4" className='user-management-title'>
              User Management
            </Typography>
            <Typography variant="body1" className='user-management-subtitle'>
              Manage your team members and their account permissions.
            </Typography>
          </Box>
          <Box className='user-management-header-actions'>
            <Box className='user-management-filters'>
              <TextField
                className='user-search-input'
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ flex: 1, maxWidth: '400px' }}
              />
              <FormControl className='role-filter-select' sx={{ minWidth: 150 }}>
                <InputLabel id="role-filter-label">All Roles</InputLabel>
                <Select
                  labelId="role-filter-label"
                  id="role-filter"
                  value={roleFilter}
                  label="All Roles"
                  onChange={(e) => setRoleFilter(e.target.value)}
                  renderValue={(value) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FilterListIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                      {value === "all" ? "All Roles" :
                       value === "SUPER_ADMIN" ? "Super Admin" :
                       value === "ADMIN" ? "Admin" :
                       value === "RAPPORTEUR" ? "Rapporteur" :
                       value === "VIEWER" ? "Viewer" : value}
                    </Box>
                  )}
                >
                  <MenuItem value="all">All Roles</MenuItem>
                  {uniqueRoles.map((role) => (
                    <MenuItem key={role} value={role}>
                      {role === "SUPER_ADMIN" ? "Super Admin" :
                       role === "ADMIN" ? "Admin" :
                       role === "RAPPORTEUR" ? "Rapporteur" :
                       role === "VIEWER" ? "Viewer" : role}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Button
              variant='contained'
              className='create-user-button'
              startIcon={<AddIcon />}
              onClick={handleNewUserButtonClick}
            >
              Create User
            </Button>
          </Box>
        </Box>

        {/* Table Section */}
        <Box className='user-management-table-container'>
          <UserManagementTable users={filteredUsers} onRefresh={getUsers} />
        </Box>
      </Box>
      <UserFormDilogContainer onRefresh={getUsers} />
      <LoaderWithText
        isOpen={loading}
        onClose={dummyModalCloseTw}
      />
    </>
  );
};

export default UserManagementContainer;

